import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"

import { NUMBER, SCIENTIFIC } from "const"
import { LangType, StoreType, UserSettings } from "client/store"
import { SilProbabilityFormat } from "client/utils"

import FormatInput from "./input"
import FormatView from "./view"


const format = (
    data: string | number | false,
    probability: SilProbabilityFormat,
    probabilityDecimal: number,
) => {
    const value = typeof data === "string" ? parseFloat(data) : data
    if (value === undefined) {
        return null
    }
    if (value === null) { // not used
        return null
    }
    if (value === false || Number.isNaN(value) || value < 0) { // incorrect value
        return false
    }

    if (probability === SCIENTIFIC) {
        return value.toExponential(probabilityDecimal)
    }
    if (probability === NUMBER) {
        return value.toFixed(probabilityDecimal)
    }

    return null
}


export const FormatProbabilityString = (
    data: string | number | false,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const { probability, probabilityDecimal } = userSettings.sil
    const result = format(data, probability, probabilityDecimal)

    if (result === null) return lang.notUsed
    if (result === false) return lang.insufficientData
    return result
}


interface FormatProps {
    title?: string
    data: string | number | false
    tableView?: boolean
    inputView?: boolean
    validation?: string
    onChange?: (value: string) => void
}

const FormatProbability = ({
    title = null,
    data,
    tableView = false,
    inputView = false,
    validation = null,
    onChange = null,
}: FormatProps) => {
    const silSettings = useSelector((state: StoreType) => state.system.userSettings.sil)
    const { probability, probabilityDecimal } = silSettings

    const [formatted, setFormatted] = useState<string | false>("")

    useEffect(() => {
        setFormatted(format(data, probability, probabilityDecimal))
    }, [data, probability, probabilityDecimal])

    const onChangeFormatted: React.ChangeEventHandler<HTMLInputElement> = (e) => setFormatted(e.target.value)
    const onChangeNull = () => {
        onChange(null)
        setFormatted(null)
    }
    const onChangeResult = (value: number) => {
        onChange(value.toExponential())
        setFormatted(format(value, probability, probabilityDecimal))
    }


    if (probability === SCIENTIFIC) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    if (probability === NUMBER) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    return null
}

export default FormatProbability
